import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CategoryIcon from '@mui/icons-material/Category';
import { useGetProductsByCategoryTreeQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function ProductsByCategory() {
  const { data: categoryTree, isLoading, error } = useGetProductsByCategoryTreeQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  if (isLoading)
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  if (error)
    return (
      <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
        Error loading products
      </Typography>
    );

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 600 }}>
        Products by Category
      </Typography>

      {categoryTree?.length > 0 ? (
        categoryTree.map((mainCategory) => (
          <Accordion
            key={mainCategory.id}
            sx={{
              mb: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                borderRadius: '8px 8px 0 0',
                '&:hover': { bgcolor: 'primary.dark' },
                '& .MuiAccordionSummary-content': {
                  alignItems: 'center',
                },
              }}
            >
              <CategoryIcon sx={{ mr: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {mainCategory.name} {mainCategory.code && `(${mainCategory.code})`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {mainCategory.products?.length > 0 && (
                  <Card sx={{ mb: 3, p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
                    >
                      General
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{`Cost Price (${currency})`}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{`Selling Price (${currency})`}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {mainCategory.products.map((product) => (
                              <TableRow key={product.id} hover>
                                <TableCell>{product.sku}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{product.name}</TableCell>
                                <TableCell>{product.barcode || '-'}</TableCell>
                                <TableCell>{product.costPrice}</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {product.sellingPrice}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={product.isActive ? 'Active' : 'Inactive'}
                                    color={product.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                  </Card>
              )}
              {mainCategory.subcategories?.length > 0 ? (
                mainCategory.subcategories.map((subcategory) => (
                  <Card key={subcategory.id} sx={{ mb: 3, p: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}
                    >
                      {subcategory.name} {subcategory.code && `(${subcategory.code})`}
                    </Typography>
                    {subcategory.products && subcategory.products.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                              <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Barcode</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{`Cost Price (${currency})`}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{`Selling Price (${currency})`}</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subcategory.products.map((product) => (
                              <TableRow key={product.id} hover>
                                <TableCell>{product.sku}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{product.name}</TableCell>
                                <TableCell>{product.barcode || '-'}</TableCell>
                                <TableCell>{product.costPrice}</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {product.sellingPrice}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={product.isActive ? 'Active' : 'Inactive'}
                                    color={product.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No products in this subcategory
                      </Typography>
                    )}
                  </Card>
                ))
              ) : (!mainCategory.products || mainCategory.products.length === 0) ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No products or subcategories
                </Typography>
              ) : null}
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No categories found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create categories and products to see them organized here
          </Typography>
        </Card>
      )}
    </Box>
  );
}

export default ProductsByCategory;
